import { useState } from 'react';
import {
  HiUser, HiLockClosed, HiOfficeBuilding,
  HiBell, HiExclamation, HiCheck, HiCreditCard, HiMail,
} from 'react-icons/hi';
import toast from 'react-hot-toast';
import Layout from '../components/Layout';
import { supabase } from '../supabase/client';
import { useAuth } from '../context/AuthContext';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function SectionCard({ title, desc, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="px-6 py-5 border-b border-gray-50">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {desc && <p className="text-xs text-gray-400 mt-0.5">{desc}</p>}
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
        {label}
      </label>
      {children}
      {hint && <p className="mt-1.5 text-xs text-gray-400">{hint}</p>}
    </div>
  );
}

function inputCls(error) {
  return [
    'w-full px-3 py-2.5 border rounded-xl text-sm text-gray-900 placeholder-gray-400',
    'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition',
    error ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300',
  ].join(' ');
}

function SaveButton({ saving, label = 'Save Changes' }) {
  return (
    <button
      type="submit"
      disabled={saving}
      className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white
        text-sm font-semibold px-5 py-2.5 rounded-xl transition shadow-sm disabled:opacity-60"
    >
      {saving ? (
        <>
          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          Saving…
        </>
      ) : (
        <><HiCheck className="w-4 h-4" /> {label}</>
      )}
    </button>
  );
}

// ─── Toggle Switch ─────────────────────────────────────────────────────────────

function Toggle({ checked, onChange, label, desc }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
      <div>
        <p className="text-sm font-medium text-gray-800">{label}</p>
        {desc && <p className="text-xs text-gray-400 mt-0.5">{desc}</p>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative inline-flex w-10 h-5.5 flex-shrink-0 rounded-full border-2 border-transparent
          transition-colors duration-200 focus:outline-none
          ${checked ? 'bg-indigo-600' : 'bg-gray-200'}`}
        style={{ height: '22px', width: '40px' }}
      >
        <span
          className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow
            transform transition duration-200 mt-px
            ${checked ? 'translate-x-4' : 'translate-x-0.5'}`}
        />
      </button>
    </div>
  );
}

// ─── Sections ─────────────────────────────────────────────────────────────────

function ProfileSection({ user }) {
  const meta  = user?.user_metadata ?? {};
  const [name,   setName]   = useState(meta.full_name ?? '');
  const [email,  setEmail]  = useState(user?.email ?? '');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!name.trim())  errs.name  = 'Name is required';
    if (!email.trim()) errs.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) errs.email = 'Enter a valid email';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    const updates = {};
    if (email !== user.email)         updates.email = email;
    if (name  !== meta.full_name)     updates.data  = { ...meta, full_name: name };

    if (Object.keys(updates).length) {
      const { error } = await supabase.auth.updateUser(updates);
      if (error) toast.error(error.message);
      else toast.success('Profile updated!');
    }
    setSaving(false);
  };

  return (
    <SectionCard title="Profile" desc="Your name and email address">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Full Name" hint={errors.name}>
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setErrors(p => ({...p, name: ''})); }}
              placeholder="John Smith"
              className={inputCls(errors.name)}
            />
            {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
          </Field>
          <Field label="Email Address">
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setErrors(p => ({...p, email: ''})); }}
              placeholder="you@example.com"
              className={inputCls(errors.email)}
            />
            {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
          </Field>
        </div>

        {/* Avatar preview */}
        <div className="flex items-center gap-4 pt-2">
          <div className="w-14 h-14 bg-indigo-600 rounded-2xl flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-xl">
              {(name || email || '?')[0].toUpperCase()}
            </span>
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-800">{name || 'Your Name'}</p>
            <p className="text-xs text-gray-400">{email}</p>
          </div>
        </div>

        <div className="pt-2">
          <SaveButton saving={saving} />
        </div>
      </form>
    </SectionCard>
  );
}

function PasswordSection() {
  const [password, setPassword] = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [saving,   setSaving]   = useState(false);
  const [errors,   setErrors]   = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!password)                        errs.password = 'New password is required';
    else if (password.length < 6)         errs.password = 'Minimum 6 characters';
    if (password !== confirm)             errs.confirm  = 'Passwords do not match';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password });
    if (error) toast.error(error.message);
    else {
      toast.success('Password updated!');
      setPassword(''); setConfirm('');
    }
    setSaving(false);
  };

  return (
    <SectionCard title="Change Password" desc="Use a strong password with at least 6 characters">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="New Password">
            <input
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setErrors(p => ({...p, password: ''})); }}
              placeholder="••••••••"
              className={inputCls(errors.password)}
            />
            {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password}</p>}
          </Field>
          <Field label="Confirm Password">
            <input
              type="password"
              value={confirm}
              onChange={(e) => { setConfirm(e.target.value); setErrors(p => ({...p, confirm: ''})); }}
              placeholder="••••••••"
              className={inputCls(errors.confirm)}
            />
            {errors.confirm && <p className="mt-1 text-xs text-red-500">{errors.confirm}</p>}
          </Field>
        </div>
        <div className="pt-2">
          <SaveButton saving={saving} label="Update Password" />
        </div>
      </form>
    </SectionCard>
  );
}

function BusinessSection({ user }) {
  const meta = user?.user_metadata ?? {};
  const [bizName,    setBizName]    = useState(meta.business_name    ?? '');
  const [bizAddress, setBizAddress] = useState(meta.business_address ?? '');
  const [bizPhone,   setBizPhone]   = useState(meta.business_phone   ?? '');
  const [bizWebsite, setBizWebsite] = useState(meta.business_website ?? '');
  const [saving,     setSaving]     = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const { error } = await supabase.auth.updateUser({
      data: {
        ...meta,
        business_name:    bizName.trim(),
        business_address: bizAddress.trim(),
        business_phone:   bizPhone.trim(),
        business_website: bizWebsite.trim(),
      },
    });
    if (error) toast.error(error.message);
    else toast.success('Business info saved!');
    setSaving(false);
  };

  return (
    <SectionCard
      title="Business Information"
      desc="Appears on invoices and contracts sent to clients"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Business Name">
            <input
              type="text"
              value={bizName}
              onChange={(e) => setBizName(e.target.value)}
              placeholder="Acme Studios"
              className={inputCls(false)}
            />
          </Field>
          <Field label="Phone">
            <input
              type="tel"
              value={bizPhone}
              onChange={(e) => setBizPhone(e.target.value)}
              placeholder="+1 555 0100"
              className={inputCls(false)}
            />
          </Field>
          <Field label="Website">
            <input
              type="url"
              value={bizWebsite}
              onChange={(e) => setBizWebsite(e.target.value)}
              placeholder="https://yoursite.com"
              className={inputCls(false)}
            />
          </Field>
          <Field label="Address">
            <input
              type="text"
              value={bizAddress}
              onChange={(e) => setBizAddress(e.target.value)}
              placeholder="123 Main St, City, State"
              className={inputCls(false)}
            />
          </Field>
        </div>
        <div className="pt-2">
          <SaveButton saving={saving} />
        </div>
      </form>
    </SectionCard>
  );
}

function NotificationsSection({ user }) {
  const meta = user?.user_metadata ?? {};
  const [prefs, setPrefs] = useState({
    invoice_reminders:  meta.notif_invoice_reminders  ?? true,
    payment_received:   meta.notif_payment_received   ?? true,
    contract_signed:    meta.notif_contract_signed    ?? true,
    weekly_summary:     meta.notif_weekly_summary     ?? false,
  });
  const [saving, setSaving] = useState(false);

  const toggle = (key) => setPrefs(p => ({ ...p, [key]: !p[key] }));

  const handleSave = async () => {
    setSaving(true);
    const { error } = await supabase.auth.updateUser({
      data: {
        ...meta,
        notif_invoice_reminders: prefs.invoice_reminders,
        notif_payment_received:  prefs.payment_received,
        notif_contract_signed:   prefs.contract_signed,
        notif_weekly_summary:    prefs.weekly_summary,
      },
    });
    if (error) toast.error(error.message);
    else toast.success('Notification preferences saved!');
    setSaving(false);
  };

  return (
    <SectionCard title="Notifications" desc="Choose what emails you want to receive">
      <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
      <div className="space-y-0">
        <Toggle
          checked={prefs.invoice_reminders}
          onChange={() => toggle('invoice_reminders')}
          label="Invoice Payment Reminders"
          desc="Get notified when you send a payment reminder"
        />
        <Toggle
          checked={prefs.payment_received}
          onChange={() => toggle('payment_received')}
          label="Payment Received"
          desc="Get notified when a client pays an invoice"
        />
        <Toggle
          checked={prefs.contract_signed}
          onChange={() => toggle('contract_signed')}
          label="Contract Signed"
          desc="Get notified when a client signs a contract"
        />
        <Toggle
          checked={prefs.weekly_summary}
          onChange={() => toggle('weekly_summary')}
          label="Weekly Summary"
          desc="Receive a weekly overview of your business activity"
        />
      </div>
      <div className="pt-5">
        <SaveButton saving={saving} label="Save Preferences" />
      </div>
      </form>
    </SectionCard>
  );
}

function PaymentsSection({ user }) {
  const meta = user?.user_metadata ?? {};
  const [stripeSecretKey,      setStripeSecretKey]      = useState(meta.stripe_secret_key      ?? '');
  const [stripePublishableKey, setStripePublishableKey] = useState(meta.stripe_publishable_key ?? '');
  const [saving,   setSaving]   = useState(false);
  const [testing,  setTesting]  = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [errors,   setErrors]   = useState({});

  const isConnected = !!meta.stripe_secret_key;

  const handleSave = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!stripeSecretKey.startsWith('sk_'))      errs.stripeSecretKey      = 'Must start with sk_test_ or sk_live_';
    if (!stripePublishableKey.startsWith('pk_')) errs.stripePublishableKey = 'Must start with pk_test_ or pk_live_';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    const { error } = await supabase.auth.updateUser({
      data: { ...meta, stripe_secret_key: stripeSecretKey, stripe_publishable_key: stripePublishableKey, setup_complete: true },
    });
    if (error) toast.error(error.message);
    else { toast.success('Stripe keys saved!'); setTestResult(null); }
    setSaving(false);
  };

  const handleTest = () => {
    setTesting(true);
    setTestResult(null);
    setTimeout(() => {
      if (stripeSecretKey.startsWith('sk_') && stripePublishableKey.startsWith('pk_')) {
        setTestResult({ ok: true, msg: 'Keys look valid! Format is correct.' });
      } else {
        setTestResult({ ok: false, msg: 'Invalid key format. Check your keys.' });
      }
      setTesting(false);
    }, 800);
  };

  return (
    <SectionCard title="Stripe Payments" desc="Stripe keys — invoice payments go directly to your bank account">
      {isConnected && (
        <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-100 rounded-xl mb-4">
          <HiCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
          <p className="text-sm font-semibold text-green-800">Stripe connected</p>
        </div>
      )}
      {testResult && (
        <div className={`flex items-center gap-2 p-3 rounded-xl mb-4 text-sm font-medium
          ${testResult.ok ? 'bg-green-50 border border-green-100 text-green-700' : 'bg-red-50 border border-red-100 text-red-600'}`}>
          {testResult.ok ? <HiCheck className="w-4 h-4 flex-shrink-0" /> : '✕'} {testResult.msg}
        </div>
      )}
      <form onSubmit={handleSave} className="space-y-4">
        <Field label="Stripe Secret Key" hint="sk_test_... or sk_live_...">
          <input type="password" value={stripeSecretKey}
            onChange={e => { setStripeSecretKey(e.target.value); setErrors(p => ({...p, stripeSecretKey: ''})); setTestResult(null); }}
            placeholder="sk_test_..." className={inputCls(errors.stripeSecretKey)} />
          {errors.stripeSecretKey && <p className="mt-1 text-xs text-red-500">{errors.stripeSecretKey}</p>}
        </Field>
        <Field label="Stripe Publishable Key" hint="pk_test_... or pk_live_...">
          <input type="text" value={stripePublishableKey}
            onChange={e => { setStripePublishableKey(e.target.value); setErrors(p => ({...p, stripePublishableKey: ''})); setTestResult(null); }}
            placeholder="pk_test_..." className={inputCls(errors.stripePublishableKey)} />
          {errors.stripePublishableKey && <p className="mt-1 text-xs text-red-500">{errors.stripePublishableKey}</p>}
        </Field>
        <div className="flex gap-3 pt-2">
          <SaveButton saving={saving} label="Save Keys" />
          <button type="button" onClick={handleTest} disabled={testing || !stripeSecretKey || !stripePublishableKey}
            className="px-4 py-2 text-sm font-semibold text-indigo-600 border border-indigo-200
              hover:bg-indigo-50 rounded-xl transition disabled:opacity-50">
            {testing ? 'Testing…' : 'Test Connection'}
          </button>
        </div>
      </form>
    </SectionCard>
  );
}

function EmailsSection({ user }) {
  const meta = user?.user_metadata ?? {};
  const [resendApiKey, setResendApiKey] = useState(meta.resend_api_key ?? '');
  const [fromEmail,    setFromEmail]    = useState(meta.from_email     ?? '');
  const [saving,   setSaving]   = useState(false);
  const [testing,  setTesting]  = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [errors,   setErrors]   = useState({});

  const isConnected = !!meta.resend_api_key;

  const handleSave = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!resendApiKey.startsWith('re_')) errs.resendApiKey = 'Must start with re_';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    const { error } = await supabase.auth.updateUser({
      data: { ...meta, resend_api_key: resendApiKey, from_email: fromEmail || 'onboarding@resend.dev' },
    });
    if (error) toast.error(error.message);
    else { toast.success('Email settings saved!'); setTestResult(null); }
    setSaving(false);
  };

  const handleTest = () => {
    setTesting(true);
    setTestResult(null);
    setTimeout(() => {
      if (resendApiKey.startsWith('re_')) {
        setTestResult({ ok: true, msg: 'Resend key format is valid!' });
      } else {
        setTestResult({ ok: false, msg: 'Invalid key. Must start with re_' });
      }
      setTesting(false);
    }, 800);
  };

  return (
    <SectionCard title="Email Settings" desc="Resend key — client emails are sent from your account">
      {isConnected && (
        <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-100 rounded-xl mb-4">
          <HiCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
          <p className="text-sm font-semibold text-green-800">Resend connected</p>
        </div>
      )}
      {testResult && (
        <div className={`flex items-center gap-2 p-3 rounded-xl mb-4 text-sm font-medium
          ${testResult.ok ? 'bg-green-50 border border-green-100 text-green-700' : 'bg-red-50 border border-red-100 text-red-600'}`}>
          {testResult.ok ? <HiCheck className="w-4 h-4 flex-shrink-0" /> : '✕'} {testResult.msg}
        </div>
      )}
      <form onSubmit={handleSave} className="space-y-4">
        <Field label="Resend API Key" hint="Found at resend.com → API Keys">
          <input type="password" value={resendApiKey}
            onChange={e => { setResendApiKey(e.target.value); setErrors(p => ({...p, resendApiKey: ''})); setTestResult(null); }}
            placeholder="re_..." className={inputCls(errors.resendApiKey)} />
          {errors.resendApiKey && <p className="mt-1 text-xs text-red-500">{errors.resendApiKey}</p>}
        </Field>
        <Field label="From Email" hint="Use onboarding@resend.dev for testing, or your verified domain for production">
          <input type="email" value={fromEmail}
            onChange={e => setFromEmail(e.target.value)}
            placeholder="you@yourdomain.com" className={inputCls(false)} />
        </Field>
        <div className="flex gap-3 pt-2">
          <SaveButton saving={saving} label="Save Keys" />
          <button type="button" onClick={handleTest} disabled={testing || !resendApiKey}
            className="px-4 py-2 text-sm font-semibold text-indigo-600 border border-indigo-200
              hover:bg-indigo-50 rounded-xl transition disabled:opacity-50">
            {testing ? 'Testing…' : 'Test Connection'}
          </button>
        </div>
      </form>
    </SectionCard>
  );
}

function DangerSection({ signOut }) {
  const [confirming, setConfirming] = useState(false);

  return (
    <SectionCard title="Danger Zone" desc="Irreversible actions — proceed with caution">
      <div className="space-y-4">

        {/* Sign out */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
          <div>
            <p className="text-sm font-semibold text-gray-800">Sign Out</p>
            <p className="text-xs text-gray-400 mt-0.5">Sign out of your PortalFlow account</p>
          </div>
          <button
            onClick={signOut}
            className="px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-200
              hover:bg-gray-100 rounded-xl transition"
          >
            Sign Out
          </button>
        </div>

        {/* Delete account */}
        <div className="flex items-center justify-between p-4 bg-red-50 border border-red-100 rounded-xl">
          <div>
            <p className="text-sm font-semibold text-red-700">Delete Account</p>
            <p className="text-xs text-red-400 mt-0.5">
              Permanently delete your account and all data
            </p>
          </div>
          {!confirming ? (
            <button
              onClick={() => setConfirming(true)}
              className="px-4 py-2 text-sm font-semibold text-red-600 border border-red-200
                hover:bg-red-100 rounded-xl transition"
            >
              Delete
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setConfirming(false)}
                className="px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                onClick={() => toast.error('Please contact support to delete your account.')}
                className="px-3 py-2 text-xs font-semibold text-white bg-red-500 hover:bg-red-600 rounded-lg transition"
              >
                Confirm Delete
              </button>
            </div>
          )}
        </div>

      </div>
    </SectionCard>
  );
}

// ─── Sidebar nav ──────────────────────────────────────────────────────────────

const TABS = [
  { id: 'profile',       icon: HiUser,           label: 'Profile' },
  { id: 'password',      icon: HiLockClosed,     label: 'Password' },
  { id: 'business',      icon: HiOfficeBuilding, label: 'Business' },
  { id: 'payments',      icon: HiCreditCard,     label: 'Payments' },
  { id: 'emails',        icon: HiMail,           label: 'Emails' },
  { id: 'notifications', icon: HiBell,           label: 'Notifications' },
  { id: 'danger',        icon: HiExclamation,    label: 'Danger Zone', red: true },
];

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function Settings() {
  const { user, signOut } = useAuth();
  const [active, setActive] = useState(() => {
    const tab = new URLSearchParams(window.location.search).get('tab');
    return tab || 'profile';
  });

  return (
    <Layout title="Settings">

      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900">Settings</h2>
        <p className="text-sm text-gray-500 mt-0.5">Manage your account and preferences</p>
      </div>

      <div className="flex flex-col md:flex-row gap-6 items-start">

        {/* Sidebar nav */}
        <div className="w-full md:w-52 flex-shrink-0">
          <nav className="bg-white rounded-2xl border border-gray-100 shadow-sm p-2 flex flex-row md:flex-col gap-1">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = active === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActive(tab.id)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium
                    transition text-left w-full
                    ${isActive
                      ? tab.red ? 'bg-red-50 text-red-600' : 'bg-indigo-50 text-indigo-700'
                      : tab.red ? 'text-red-500 hover:bg-red-50' : 'text-gray-600 hover:bg-gray-50'
                    }`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {active === 'profile'       && <ProfileSection       user={user} />}
          {active === 'password'      && <PasswordSection />}
          {active === 'business'      && <BusinessSection      user={user} />}
          {active === 'payments'      && <PaymentsSection      user={user} />}
          {active === 'emails'        && <EmailsSection        user={user} />}
          {active === 'notifications' && <NotificationsSection user={user} />}
          {active === 'danger'        && <DangerSection        signOut={signOut} />}
        </div>

      </div>
    </Layout>
  );
}
